/* eslint-disable @typescript-eslint/require-await */
// @ts-strict-ignore

/**
 * @module BubbleConferences
 */
import { JingleSession } from 'rainbow-web-sdk/libs/strophe/strophe.jingle.session';
import { Bubble, BubbleRB } from 'rainbow-web-sdk/models/bubble.model';
import { BubbleConferenceParticipant } from 'rainbow-web-sdk/models/webConferenceParticipant.model';
import { Subject, Subscription, debounceTime, filter } from 'rxjs';
import { RBEvent } from './event.model';
import { BubbleConferenceService, LOGGER_SVC, LoggerService, MainService } from 'rainbow-web-sdk/services';
import { BUBBLE_CONFERENCE_SVC } from '../services/conference/bubbleConference.service';
import { BubbleConferenceRecapParams, BubbleConferenceRecapService, CONFERENCE_RECAP_SVC } from '../services/conference/bubbleConferenceRecap.service';
import { DataChannel, DataChannelEvents, DataChannelStatus } from './dataChannel.model';
import { format } from 'date-fns';

/**
 * BubbleConference public events
 * @eventProperty
 */
export enum BubbleConferenceEvents {
    /**
    * @eventProperty
    * This RBEvent is fired when the bubbleConference status is updated
    */
    ON_STATUS_CHANGE = "ON_STATUS_CHANGE",

    /**
    * @eventProperty
    * This RBEvent is triggered when the mute status of a conference participant (including the connectedUser) changes.
    */
    ON_PARTICIPANT_MUTE_CHANGE = "ON_PARTICIPANT_MUTE_CHANGE",

    /**
     * @eventProperty
     * This RB event is fired when there's a modification in the list of the participants (participants are added or removed)
     */
    ON_PARTICIPANT_LIST_CHANGE = "ON_PARTICIPANT_LIST_CHANGE",

    /**
     * @eventProperty
     * This RB event is fired when there's a modification in the participants raisehand status
     */
    ON_PARTICIPANT_RAISEHAND_CHANGE = "ON_PARTICIPANT_RAISEHAND_CHANGE",

    /**
     * @eventProperty
     * This RB event is fired when there's a modification in the connected user local media
     */
    ON_LOCAL_PARTICIPANT_MEDIA_CHANGE = "ON_LOCAL_PARTICIPANT_MEDIA_CHANGE",

    /**
     * @eventProperty
     * This RB event is fired when there's a modification on remote participant media
     */
    ON_REMOTE_MEDIA_CHANGE = "ON_REMOTE_MEDIAS_CHANGE",

    /**
     * @eventProperty
     * This RBEvent is send when a liveTranscription is ready and activated
     */
    ON_LIVE_TRANSCRIPTION_ACTIVATED = 'ON_LIVE_TRANSCRIPTION_ACTIVATED',

    /**
     * @eventProperty
     * This RBEvent is send when a liveTranscription is updated
     * - the list of active languages has changed
     * - the liveTranscriptionUsedBySomeone has changed
     */
    ON_LIVE_TRANSCRIPTION_UPDATE = 'ON_LIVE_TRANSCRIPTION_UPDATE',

    /**
     * @eventProperty
     * This RBEvent is send when a liveTranscription is being started
     */
    ON_LIVE_TRANSCRIPTION_ACTIVATING = 'ON_LIVE_TRANSCRIPTION_ACTIVATING',

    /**
     * @eventProperty
     * This RBEvent is send when a liveTranscription is stopped
     */
    ON_LIVE_TRANSCRIPTION_STOPPED = 'ON_LIVE_TRANSCRIPTION_STOPPED',

    /**
     * @eventProperty
     * **Event Data**
     * - **message**: The new message received
     * This RBEvent is send when a we receive a liveTranscription message
     */
    ON_LIVE_TRANSCRIPTION_MESSAGE_RECEIVED = 'ON_LIVE_TRANSCRIPTION_MESSAGE_RECEIVED',

    /**
     * @eventProperty
     * **Event Data**
     * This RBEvent is send when there's a change on the recording state for this conference
     */
    ON_RECORDING_UPDATE = 'ON_RECORDING_UPDATE',

    /**
    * @eventProperty
    * * **Event Data**
    * - **participant**: the current participant talking. It could be null / undefined if noone is talking currently;
    * This RBEvent is triggered when the current talker changes.
    */
    ON_TALKER_EVENT = "ON_TALKER_EVENT"
}

export enum BubbleConferenceVideoQuality {
    HIGH = 2,
    MEDIUM = 1,
    LOW = 0
}

/**
 * This enum represents the BubbleConference statuses
 */
export enum BubbleConferenceStatus {
    UNJOINED = "unjoined",
    CONNECTING = "connecting",
    CONNECTED = "connected",
    ON_HOLD = "onhold",
    RECONNECTING = "reconnecting",
    ENDED = "ended"
}

/**
 * This enum lists the BubbleConference media
 */
export enum BubbleConferenceMedia {
    VIDEO = "video",
    SHARING = "sharing",
    MOZAIC = "mozaic",
    DYNAMIC_FEED = "dynamicFeed"
}

/**
 * This enum lists the BubbleConference media action
 */
export enum BubbleConferenceMediaAction {
    ADDED = "added",
    REMOVED = "removed",
    HOLD = "hold"
}

export interface LiveTranscriptionLanguage {
    /* iso code of the language */
    iso: string;
    /* native name of the language */
    native: string;

    /* translated name of the language (if available) */
    translated: string;

    /* TRUE if it's RTL type of language */
    inverted: boolean;
}

export class LiveTranscriptionMessage {
    public date: string;
    public text: string;
    public from?: string;
    // public language?: string;
    // public translatedText?: string;

    public static create(date: Date, text: string, from?: string): LiveTranscriptionMessage {
        const transcriptionMessage = new LiveTranscriptionMessage();
        transcriptionMessage.date = format(date, 'HH:mm:ss');
        transcriptionMessage.text = text;
        transcriptionMessage.from = from;
        // try {
        //     const parsed = JSON.parse(text);
        //     transcriptionMessage.translatedText = parsed?.translation?.text;
        //     transcriptionMessage.language = parsed?.translation?.language;

        //     //add transcritpion
        //     if (!parsed?.translation) {
        //         //find the transcription text
        //         transcriptionMessage.translatedText = parsed?.transcription?.text;
        //         transcriptionMessage.language = parsed?.transcription?.language;
        //     }
        // }
        // catch {
        //     //do nothing
        // }
        return transcriptionMessage;
    }
}

/**
 * This interface is used to specify bubbleConference join parameters
 */
export interface BubbleConferenceJoinParameters {
    /**
     * If true, the user will join with no audio.
     * He'll be able to see all videos / sharing and also share his video, but the audio sessions will be "empty".
     */
    noAudio?: boolean;

    /**
     * If set to true, the user will join the conference as an attendee.
     * In other words, he'll receive the audio and video from the conference
     * but won't transmit anything of his own. No access to his microphone and/or camera will be required.
     */
    attendeeMode?: boolean;

    /**
     * If set to true deskphone (only for RB Hub users).
     * The user will be call on it's deskphone to join the audio conference.
     */
    joinByPhone?: boolean;

    /**
     * If set to true, it permits to join several conferences.
     */
    joinMultiple?: boolean;
}

/**
 * This interface is used to represent a bubble conference containing
 * all the relevant information to the current conference session.
 */
export interface BubbleConference {

    /**
     * Global reference of the conference
     * @readonly
     */
    id: string;

    /**
     * The Bubble object related to the conference
     * @readonly
     */
    bubble: Bubble;

    /**
     * The current status of the conference.
     * @readonly
     */
    status: BubbleConferenceStatus;

    /**
     * The current duration of the conference (in seconds)
     * @readonly
     */
    duration: number;

    /**
     * Whether this is my conference (owner of the conference)
     * @readonly
     */
    isMyConference: boolean;

    /**
     * Whether I have moderator rights
     * @readonly
     */
    isModerator: boolean;

    /**
     * Whether this a specific conference for media echo test
     * @readonly
     */
    isMediaEchoTestConference: boolean;

    /**
     * Yourself in the conference
     * @readonly
     */
    localParticipant: BubbleConferenceParticipant;

    /** List of other participants currently connected in the conference */
    participants: BubbleConferenceParticipant[];

    /**
     * TRUE if the current user is sharing his screen at the moment
     */
    hasLocalSharing: boolean;

    /** True when a recap recording is in progress */
    isRecapRecording: boolean;

    /** True when a recap recording is in progress and paused */
    isRecapRecordingPaused: boolean;

    /**
    * Subscribe to events on the webConference (all events are of RBEvent type);
    * @param handler - The call-back function that will be subscribed to the RxJS subject
    * @param filterEvent - a list of event names that the user wants to receive when subscribing (used a filter)
    */
    subscribe(handler: (event: RBEvent<BubbleConferenceEvents>) => any, filterEvent?: BubbleConferenceEvents | BubbleConferenceEvents[], debounce?: number): Subscription;

    /**
     * Join this conference
     * @param joinParams - the join paramters
     */
    join(joinParams?: BubbleConferenceJoinParameters): Promise<void>;

    /**
     * Removes connected user from this conference.
     */
    leave(): Promise<void>;

    /**
     * Stop this conference
     */
    stop(): Promise<void>;

    /**
     * Mute the microphone of the current participant
     */
    mute(): Promise<void>;

    /**
     * Unmute a conference
     */
    unmute(): Promise<void>;

    /**
     * Add media to a conference
     * @param mediaType - the mediaType to add to the bubbleConference
     */
    addMedia(mediaType: BubbleConferenceMedia): Promise<void>;

    /**
     * Remove media from a conference
     * @param mediaType - the mediaType to add to the bubbleConference
     */
    removeMedia(mediaType: BubbleConferenceMedia): Promise<void>;

    /**
     * Utility method to attach conference media to a video element
     * @param videoElementId - the HTML video element id
     * @param mediaType - the type of media to attach
     * @param participant - the participant
     */
    attachMediaToHtmlElement(videoElementId: string, mediaType: BubbleConferenceMedia, participant?: BubbleConferenceParticipant): void;

    /**
     * Utility method to detach all media from a video element
     * @param videoElementId - the HTML video element id
     */
    detachMediaToHtmlElement(videoElementId: string): void;

    /**
     * Utility method to ask the server to change the incoming video quality for given participant;
     * NOTE: The quality depends also on the uplink of the publisher, so even when the asked quality is High, the quality is still dependant on the network condition
     * of both parties (publisher and subscriber)
     * NOTE: This API will throw error if no video session is available for this participant;
     * @param quality - the wanted quality that we want to receive for this participant
     * @param participant - the related participant that is publishing video; If UNDEFINED, then we'll update the quality of the dynamic feed
     */
    updateVideoQualityForParticipant(quality: BubbleConferenceVideoQuality, participant?: BubbleConferenceParticipant): Promise<void>;

    /**
     * Utility method to put the incoming video session from given user ON HOLD; This means that the session is still active, but no video frames
     * are received inside. It's extremely important to manage low bandwith, or in case of having too many incoming videos;
     * This will allow to hold / unhold quickly videos from given participants in case the user is shown on the screen (or hidden), for instance, in case of grid view;
     * @param participant - the related participant that is publishing video;
     */
    holdRemoteVideo(participant: BubbleConferenceParticipant): Promise<void>;

    /**
     * Utility method to RETRIEVE the incoming video session from given user that is ON HOLD;
     * @param participant - the related participant that is publishing video;
     */
    unholdRemoteVideo(participant: BubbleConferenceParticipant): Promise<void>;

    /**
     * MODERATORS ONLY - API to mute another participant
     * @param participant - participant to be muted
     */
    muteParticipant(participant: BubbleConferenceParticipant): Promise<void>;

    /**
     * Raise your hand in the concerned conference
     */
    raiseHand(): Promise<void>;

    /**
     * Lower your hand in the concerned conference
     */
    lowerHand(): Promise<void>;

    /**
     * Get the current participant that is sharing. It could also be the current user
     * if he's the one that is sharing his screen.
     * NB We can have only 1 person that is sharing his screen at the same time !
     */
    getSharingParticipant(): BubbleConferenceParticipant;

    /**
     * Start recap recording a conference.
     * @param params - Recordings parameters
     */
    startRecap(params?: BubbleConferenceRecapParams): Promise<void>;

    /**
     * Pause recap a conference.
     */
    pauseRecap(): Promise<void>;

    /**
     * Resume recap a conference.
     */
    resumeRecap(): Promise<void>;

    /**
     * Stop recap a conference.
     */
    stopRecap(): Promise<void>;

    // CCH TODO:
    // 1. Add method to join an already started media echo bubble
    // 2. Add a method to stop an already started media echop bubble
    // 3. Add a method to subscribe to the pilotgw service (same as for large grid)

    /**
     * Json serialisation for debug purpose
     * @internal
     */
    toJSON(): any;
}
/**
 * This class is used to represent the current conference recap recording states
 */
export class BubbleConferenceRecordState {
    isRecording: boolean = true;
    recordingIsPaused: boolean = false;
    /** @internal */
    hideBanner: boolean = false;
    recordingConfig: BubbleConferenceRecapParams = undefined;
}

/**
 * This class is used to represent a web conference session containing
 * all the relevant information to the current conference session
 * @internal
 */
export class WebConferenceSession implements BubbleConference {
    /** Conference Session ID. This is a global reference of the conference session */
    public id: string;

    /** The Bubble object related to the conference */
    public bubble: BubbleRB;

    private _status: BubbleConferenceStatus = BubbleConferenceStatus.UNJOINED;
    public get status(): BubbleConferenceStatus {
        return this._status;
    }

    public set status(newStatus) {
        if (this._status !== newStatus) {
            this._status = newStatus;
            this.sendEvent(BubbleConferenceEvents.ON_STATUS_CHANGE);
        }
    }

    /** The current status of the conference. */
    // public status: BubbleConferenceStatus = BubbleConferenceStatus.UNJOINED;

    /** The current duration of the conference (in seconds) */
    public duration: number;

    /** Yourself in the conference */
    public localParticipant: BubbleConferenceParticipant;

    /** The list of other participants currently connected in the conference */
    public participants: BubbleConferenceParticipant[];

    /** Boolean indicating whether the current user is currently sharing his screen */
    public hasLocalSharing: boolean = false;

    /** The local sharing session (as a JINGLE SESSION object) or null */
    public localSharingSession: JingleSession;

    /** The Audio session related to the web conference. (represented as a Jingle Session object) */
    public audioSession: JingleSession;

    /** Object containing the talking time statistics for the conference */
    public talkingTimeStats: any = {};

    /** The ID of the participant that is currently talking */
    private _talker: string;

    public set talker(value: string) {
        if (this._talker !== value) {
            //find the current talker
            let participant = null;
            if (value) {
                participant = (value === this.localParticipant?.id) ? this.localParticipant : this.participants.find(part => part.id === value);
            }
            this._talker = value;

            this.sendEvent(BubbleConferenceEvents.ON_TALKER_EVENT, { participant });
        }
    }

    public get talker(): string {
        return this._talker;
    }

    /** Whether this is my conference (owner of the conference) */
    public isMyConference: boolean = false;

    /** Whether I have moderator rights */
    public get isModerator(): boolean { return this.bubble.isConnecteUserOrganizer(); }

    /** Whether this is a media echo test specific conference */
    public isMediaEchoTestConference: boolean = false;

    /**
     * The Dynamic feed session related to the web conference. (represented as a Jingle Session object);
     * Dynamic feed is the single video session where we see the last person that spoke in it (for all video participants in the conference)
     */
    public dynamicFeedSession: any = null;

    /** The ID of the participant that spoke before the current talker */
    public lastTalker: string = null;

    /**
     * object containing information about recording (for the "owner") of the recording
     * - isRecording (boolean) : if there's an active recording (could also be on "pause")
     * - recordingIsPaused (boolean) : if the active recording is on pause
     */
    public record: BubbleConferenceRecordState = {
        isRecording: false,
        recordingIsPaused: false,
        hideBanner: false,
        recordingConfig: undefined
    };

    /* If the live transcription is available for this conference */
    private _liveTranscriptionAvailable: boolean = false;

    /* the list of all currently active languages for the live transcription */
    public set liveTranscriptionAvailable(value: boolean) {
        this._liveTranscriptionAvailable = value;
        this.sendEvent(BubbleConferenceEvents.ON_LIVE_TRANSCRIPTION_UPDATE);
    }

    public get liveTranscriptionAvailable(): boolean {
        return this._liveTranscriptionAvailable;
    }

    /* If the live transcription is activated for this conference for this user */
    public liveTranscriptionActivated: boolean = false;

    /* If the live transcription is activated and used by someone in the conference */
    public set liveTranscriptionUsedBySomeone(value: boolean) {
        if (this._liveTranscriptionUsedBySomeone !== value) {
            this._liveTranscriptionUsedBySomeone = value;
            this.sendEvent(BubbleConferenceEvents.ON_LIVE_TRANSCRIPTION_UPDATE);
        }
    };

    public get liveTranscriptionUsedBySomeone(): boolean {
        return this._liveTranscriptionUsedBySomeone;
    }

    private _liveTranscriptionUsedBySomeone: boolean = false;

    /* the current selected language for the live transcription */
    public liveTranscriptionLanguage: LiveTranscriptionLanguage = undefined;

    /* the list of all currently active languages for the live transcription */
    public set liveTranscriptionActiveLanguages(value: string[]) {
        this._liveTranscriptionActiveLanguages = value;
        this.sendEvent(BubbleConferenceEvents.ON_LIVE_TRANSCRIPTION_UPDATE);
    }

    public get liveTranscriptionActiveLanguages(): string[] {
        return this._liveTranscriptionActiveLanguages;
    }

    private _liveTranscriptionActiveLanguages: string[] = [];

    /* the list of all currently active languages for the live transcription */
    public liveTranscriptionLanguages: LiveTranscriptionLanguage[] = [];

    /* the list of all currently active languages for the live transcription */
    public liveTranscriptionMessages: LiveTranscriptionMessage[] = [];

    private cleanString(str: string): string {
        return str.replace(/^\s*"(.*)"\s*$/, '$1');
    }

    public set liveTranscription(value) {
        if (value) {
            console.error(`liveTranscription data channel created`);
            this._liveTranscriptionDataChannel = value;
            this._dataChannelSubscritpion = this._liveTranscriptionDataChannel.subscribe((event: RBEvent<DataChannelEvents>) => {
                try {
                    //do something
                    console.error(`${event.name} ${event.data}`);
                    if (event.name === DataChannelEvents.ON_STATUS_CHANGED) {
                    //check the new status
                        const status: DataChannelStatus = event.data;
                        if (status === DataChannelStatus.CONNECTED) { this.sendEvent(BubbleConferenceEvents.ON_LIVE_TRANSCRIPTION_ACTIVATED); }
                        if (status === DataChannelStatus.CLOSED) {
                            this.liveTranscriptionActivated = false;
                            this.sendEvent(BubbleConferenceEvents.ON_LIVE_TRANSCRIPTION_STOPPED);
                        }
                    }
                    if (event.name === DataChannelEvents.ON_MESSAGE_RECEIVED) {
                        const data = JSON.parse(event.data);
                        this.liveTranscriptionMessages.push(LiveTranscriptionMessage.create(new Date(), this.cleanString(data.text)));
                        this.sendEvent(BubbleConferenceEvents.ON_LIVE_TRANSCRIPTION_MESSAGE_RECEIVED, { message: this.liveTranscriptionMessages[this.liveTranscriptionMessages.length - 1] });
                    }
                }
                catch (error) {
                    this.logger.error(`[WebConference] liveTranscription data channel subscription () -- error -- ${error?.message}`);
                }
            });
            if (this.liveTranscriptionActivated) {
                // accept data channel as it's already activated
                if (this._liveTranscriptionDataChannel?.status === DataChannelStatus.CLOSED || this._liveTranscriptionDataChannel?.status === DataChannelStatus.INVITED) {
                    this._liveTranscriptionDataChannel.accept();
                }
            }
        }
        else {
            console.error(`destroy liveTranscription`);
            this._liveTranscriptionDataChannel = undefined;
            if (this._dataChannelSubscritpion) { this._dataChannelSubscritpion.unsubscribe(); this._dataChannelSubscritpion = undefined; }
            this.liveTranscriptionActivated = false;
            this.sendEvent(BubbleConferenceEvents.ON_LIVE_TRANSCRIPTION_STOPPED);
        }
    }

    /* get the current live transcription channel where we receive the messages */
    public get liveTranscription(): DataChannel {
        return this._liveTranscriptionDataChannel;
    }

    //internal live transcription properties and subscriptions
    private _dataChannelSubscritpion: Subscription = undefined;
    private _liveTranscriptionDataChannel: DataChannel = undefined;

    /** recordingStarted If there's currently an active recording in the bubble */
    public recordingStarted: boolean = false;

    /** ID of the user that records the conference */
    public recordingUserId: string = "";

    /** Current recording state ("on" or "paused") */
    public currentRecordingState: string = "";

    /** TRUE If the user has joined using the "noAudio" option (no outgoing audio stream is send, and the incoming audio is muted) */
    public noAudio: boolean = false;

    /** TRUE If the user has joined the conference with audio on the deskphone */
    public audioOnDeskphone: boolean = false;

    /** TRUE If the user have register to liveTranscription */
    public transcriptionActivated: boolean = false;

    /** TRUE If the user have register to postTranscription */
    public postTranscriptionActivated: boolean = false;

    public get isRecapRecording() { return this.record.isRecording; }
    public get isRecapRecordingPaused() { return this.record.recordingIsPaused; }

    /**
     * Object that will contain the informations related to the large grid/ mozaic service subscription. The object is either null or in this format:
            { id --> id of the service
            sessionId --> id of the Jingle session
            videoSession --> the jingle video session itself
            }
     *
     */
    public gridServiceSubscription: any = null;

    /**
     * A configuration settings that will disable the auto subscription for new videos for this conference. All already activated videos will not be impacted;
     * This only refers to new incoming video sessions. This wil also impact the auto subscription to the dynamic feed
     */
    public disableAutoSubscriptions: boolean = false;

    /** A configuration settings that will put on hold the incoming sharing session (or any other incoming sharing session until this settings is modified again) */
    public disableIncomingSharing: boolean = false;

    /** The list of participants currently raising their hand in the conference */
    private _raiseHandParticipants: BubbleConferenceParticipant[] = [];

    public get raiseHandParticipants(): BubbleConferenceParticipant[] { return this._raiseHandParticipants; }
    public set raiseHandParticipants(participants: BubbleConferenceParticipant[]) {
        this._raiseHandParticipants = participants;
    }

    private sharingParticipant: BubbleConferenceParticipant;

    private pinnedParticipant: any;

    /*
    * PRIVATE UCAAS PARAMETERS NOT TO BE USED BY SDK
    */
    /** @internal */
    public type: string = "v2";
    /** @internal */
    public haveJoined: boolean = false;
    /** @internal */
    public isInExternalWindow: boolean = false;
    /** @internal */
    public conferenceView: string;
    /** @internal */
    public externalWindowRef: any;
    /** @internal */
    public isOnlySharingWindow: boolean = false;
    /** @internal */
    public autoPopoutSharingWindow: boolean = false;
    /** @internal */
    public autoPopoutWindow: boolean = false;
    /** @internal */
    public control = {
        activeControl: false,
        hasRemoteControlling: false,
        controller: null,
        controlled: null,
        pauseControl: null,
        giveControlFirst: null
    };

    /** @internal */
    public conferenceLayoutSize: any = {};
    /** @internal */
    public subtitlesEnable: boolean = false;
    /** @internal */
    public dimLocalSharingScreen: boolean = true;
    /** @internal */
    public localStreams: any;
    /** @internal */
    public jingleJid: string;
    /** @internal */
    public originalVideoStream: any = null;
    /** @internal */
    public metricsId: string;
    /** @internal */
    public metricsState: string;
    /** @internal */
    public networkQuality: number = 0;
    /** @internal */
    public services: any = [];
    /** @internal */
    public subscribedToGridService: boolean = false;
    /** @internal */
    public dynamicFeedSubscriptionInProgress: boolean = false;
    /** @internal */
    public locked: boolean;
    /** @internal */
    public onHold: boolean = false;

    private durationTimer: any;

    private eventSubject: Subject<RBEvent<BubbleConferenceEvents>> = new Subject<RBEvent<BubbleConferenceEvents>>();
    private logger?: LoggerService;
    private webConferenceService: any;
    private conferenceRecordingService: any;

    public static create(id: string, bubble: BubbleRB) { return new WebConferenceSession(id, bubble); }

    /** @internal */
    constructor(id: string, room: BubbleRB) {
        this.id = id;
        this.bubble = room;
        this.duration = 0;
        this.participants = [];
        this.conferenceView = "grid_view";
        this.haveJoined = false;
        this.isMyConference = false;
        this.pinnedParticipant = null;
        this.locked = false;
        this.localStreams = [];
        this.logger = MainService.getInstance().inject<LoggerService>(LOGGER_SVC);
        this.webConferenceService = MainService.getInstance().inject<BubbleConferenceService>(BUBBLE_CONFERENCE_SVC);
        this.conferenceRecordingService = MainService.getInstance().inject<BubbleConferenceRecapService>(CONFERENCE_RECAP_SVC);
    }

    // ****************************************************************** //
    // ** PUBLIC SDK API                                               ** //
    // ****************************************************************** //

    public subscribe(handler: (event: RBEvent<BubbleConferenceEvents>) => any, eventNames?: BubbleConferenceEvents | BubbleConferenceEvents[], debounce: number = 0): Subscription {
        const observable = eventNames
            ? this.eventSubject.pipe(filter((event: RBEvent<BubbleConferenceEvents>) => {
                if (eventNames instanceof Array) return eventNames.indexOf(event.name) !== -1;
                return event.name === eventNames;
            }), debounceTime(debounce))
            : this.eventSubject;

        return observable.subscribe((event: RBEvent<BubbleConferenceEvents>) => {
            try { handler(event); }
            catch (error) { this.logger.error(`[WebConference] subscribe () -- error -- ${error.message}`); }
        });
    }

    public sendEvent(name: BubbleConferenceEvents, data?: any): void {
        this.eventSubject.next(RBEvent.create(name, data));
    }

    /**
     * Joins this conference
     * @param params - the join paramters
     */
    public async join(joinParams?: BubbleConferenceJoinParameters): Promise<void> {
        if (joinParams?.joinByPhone) await this.webConferenceService.joinByPhone(this.bubble);
        await this.webConferenceService.joinWebConference(this.bubble, joinParams);
        await this.webConferenceService.waitWebConferenceConnection(this);
    }

    /**
     * Removes connected user from this conference.
     */
    public async leave(): Promise<void> {
        return this.webConferenceService.leaveWebConference(this);
    }

    /**
     * Stop this conference
     */
    public async stop(): Promise<void> {
        return this.webConferenceService.stopWebConference(this.bubble);
    }

    /**
     * Mute a conference
     */
    public mute(): Promise<void> {
        return this.webConferenceService.updateParticipant(this, this.localParticipant.id, 'mute');
    }

    /**
     * Unmute a conference
     */
    public unmute(): Promise<void> {
        return this.webConferenceService.updateParticipant(this, this.localParticipant.id, 'unmute');
    }

    /**
     * Add media
     */
    public addMedia(media: BubbleConferenceMedia): Promise<void> {
        return this.webConferenceService.addMediaToWebConference(this, media);
    }

    /**
     * Remove media
     */
    public removeMedia(media: BubbleConferenceMedia): Promise<void> {
        return this.webConferenceService.removeMediaFromWebConference(this, media);
    }

    /**
     * Start Live Transcription
     */
    public async startLiveTranscription(transcriptionLanguage?: LiveTranscriptionLanguage): Promise<void> {
        if (!this.liveTranscriptionAvailable) throw new Error("[BubbleConferenceSession] -- startLiveTranscription -- live transcription is not available");
        //if none is selected, start only transcription without translate

        if (!transcriptionLanguage && this.liveTranscriptionLanguage) {
            //reuse the old used language
            transcriptionLanguage = this.liveTranscriptionLanguage;
        }

        if (this.liveTranscriptionActivated) {
            return this.changeLiveTranscriptionLanguage(transcriptionLanguage);
        }
        try {
            this.liveTranscriptionActivated = true;

            this.sendEvent(BubbleConferenceEvents.ON_LIVE_TRANSCRIPTION_ACTIVATING);
            await this.webConferenceService.startLiveTranscription(this, transcriptionLanguage?.iso);
            this.liveTranscriptionLanguage = transcriptionLanguage;
            //if data channel is here, subscribe and then add the language;
            if (this._liveTranscriptionDataChannel?.status === DataChannelStatus.CLOSED || this._liveTranscriptionDataChannel?.status === DataChannelStatus.INVITED) {
                await this._liveTranscriptionDataChannel.accept();
            }
        }
        catch (error) {
            this.logger.error(`[BubbleConferenceSession] startLiveTranscription () -- error -- ${error?.message}`);
            this.liveTranscriptionActivated = false;
            this.sendEvent(BubbleConferenceEvents.ON_LIVE_TRANSCRIPTION_STOPPED);
            throw error;
        }
    }

    private async changeLiveTranscriptionLanguage(transcriptionLanguage?: LiveTranscriptionLanguage): Promise<void> {
        try {
            await this.webConferenceService.startLiveTranscription(this, transcriptionLanguage?.iso);
            this.liveTranscriptionLanguage = transcriptionLanguage;
            this.sendEvent(BubbleConferenceEvents.ON_LIVE_TRANSCRIPTION_ACTIVATED);
        }
        catch (error) {
            this.logger.error(`[BubbleConferenceSession] changeLiveTranscriptionLanguage () -- error -- ${error?.message}`);
            throw error;
        }
    }

    /**
     * Start Live Transcription
     */
    public async stopLiveTranscription(): Promise<void> {
        if (!this.liveTranscriptionAvailable) throw new Error("[BubbleConferenceSession] -- stopLiveTranscription -- live transcription is not available");
        //check if data channel is already available
        try {
            //kill also data channel session
            if (this._liveTranscriptionDataChannel) { this._liveTranscriptionDataChannel.delete(); }
            await this.webConferenceService.stopLiveTranscription(this);
            this.liveTranscriptionActivated = false;
        }
        catch (error) {
            this.logger.error(`[BubbleConferenceSession] stopLiveTranscription () -- error -- ${error?.message}`);
            this.liveTranscriptionActivated = false;
            throw error;
        }
    }

    public attachMediaToHtmlElement(videoElementId: string, media: BubbleConferenceMedia, participant?: BubbleConferenceParticipant): void {
        switch (media) {
            case BubbleConferenceMedia.VIDEO:
                this.webConferenceService.attachVideoSessionToHtmlElement(videoElementId, participant.videoSession);
                break;
            case BubbleConferenceMedia.SHARING:
                this.webConferenceService.attachVideoSessionToHtmlElement(videoElementId, this.getSharingSession());
                break;
            case BubbleConferenceMedia.DYNAMIC_FEED:
                this.webConferenceService.attachVideoSessionToHtmlElement(videoElementId, (this as WebConferenceSession).dynamicFeedSession);
                break;
            default: break;
        }
    }

    public detachMediaToHtmlElement(videoElementId: string): void {
        this.webConferenceService.detachMediaToHtmlElement(videoElementId);
    }

    public async updateVideoQualityForParticipant(quality: BubbleConferenceVideoQuality, participant?: BubbleConferenceParticipant): Promise<void> {
        return this.webConferenceService.updateVideoQualityForPublisher(this.id, quality, participant);
    }

    public async holdRemoteVideo(participant: BubbleConferenceParticipant): Promise<void> {
        await this.webConferenceService.putRemoteVideoSessionOnHold(participant);
    }

    public async unholdRemoteVideo(participant: BubbleConferenceParticipant): Promise<void> {
        await this.webConferenceService.putRemoteVideoSessionOnUnhold(participant);
    }

    /**
     * Mute a conference
     */
    public muteParticipant(participant: BubbleConferenceParticipant): Promise<void> {
        if (this.bubble?.isConnecteUserOrganizer()) return this.webConferenceService.updateParticipant(this, participant.id, 'mute');

        throw new Error("[BubbleConferenceSession] -- current user is not moderator !");
    }

    public raiseHand(): Promise<void> {
        return this.webConferenceService.raiseHand(this);
    }

    public lowerHand(): Promise<void> {
        return this.webConferenceService.lowerHand(this, this.localParticipant);
    }

    /**
     * As conference owner, if I leave the conference, it also ends for all the other participants.
     * To avoid this inconvenience, simply delegate the conference to another member,
     * and the conference will continue even without the presence of its creator.
     * The delegated participant will inherit the rights to end the conference.
     * @param participantId - the participantId
     */
    public async delegateToParticipant(participantId: string): Promise<void> {
        return this.webConferenceService.delegateConferenceToParticipant(this, participantId);
    }

    // ****************************************************************** //
    // ** "PRIVATE" API                                                ** //
    // ****************************************************************** //

    /** Return an array of all WebConferenceParticipants taking part of the web conference */
    public getParticipants(): BubbleConferenceParticipant[] { return this.participants; }

    /** Return the current user as a web conference participant connected to the conference*/
    public getLocalParticipant(): BubbleConferenceParticipant { return this.localParticipant; }

    /** Get the room related to the web conference session */
    public getBubble(): Bubble { return this.bubble; }

    /** Get the jingle audio session related to the conference session */
    public getAudioSession(): JingleSession { return this.audioSession; }

    /** Web conference participant object that is currently sharing (could also be myself) */
    public getSharingParticipant(): BubbleConferenceParticipant { return this.sharingParticipant; }

    public async startRecap(params?: BubbleConferenceRecapParams): Promise<void> {
        return this.conferenceRecordingService?.startRecap(this, params);
    }

    public async pauseRecap(): Promise<void> {
        return this.conferenceRecordingService?.pauseRecap(this);
    }

    public async resumeRecap(): Promise<void> {
        return this.conferenceRecordingService?.resumeRecap(this);
    }

    public async stopRecap(): Promise<void> {
        return this.conferenceRecordingService?.stopRecap(this);
    }

    /**
     * Get the remote web conference participant object that is currently sharing in the conference;
     * IF the local participant is currently the sharing participant, will return null
     */
    public getRemoteSharingParticipant(): BubbleConferenceParticipant {
        return (this.sharingParticipant && !this.hasLocalSharing) ? this.sharingParticipant : null;
    }

    /** Get the local sharing session (if any) */
    public getLocalSharingSession(): JingleSession { return this.localSharingSession; }

    /** Get the remote sharing session (or null) from the sharing participant object */
    public getRemoteSharingSession(): JingleSession | undefined {
        return (this.sharingParticipant && !this.hasLocalSharing) ? this.sharingParticipant.videoSession : undefined;
    }

    /** Get the Sharing session (mine or remote, if any) */
    public getSharingSession(): JingleSession | undefined {
        if (this.localSharingSession) return this.getLocalSharingSession();
        return this.getRemoteSharingSession();
    }

    /** Find and return a participant by his Id */
    public getParticipantById(id: string): BubbleConferenceParticipant {
        if (this.localParticipant.id === id) return this.localParticipant;
        const participant = this.participants.find((part: BubbleConferenceParticipant) => { return part.id === id; });
        return participant;
    }

    /** Find and return a participant by his contact dbId */
    public findParticipantByContactDbId(id: string): BubbleConferenceParticipant {
        if (this.localParticipant && this.localParticipant.contact.dbId === id) return this.localParticipant;
        const participant = this.participants.find((part: BubbleConferenceParticipant) => { return part.contact && part.contact.dbId === id; });
        return participant;
    }

    /** Check if the current web conference is locked. In case it's locked, no other user is allowed to join its */
    public isLocked() { return this.locked; }

    /** Return an array of WebConferenceParticipant to whom we can delegate the conference */
    public getParticipantsListWithDelegateCapability() {
        return this.participants.filter((participant) => {
            return participant.delegateCapability;
        });
    }

    /*
    * PRIVATE UCAAS FUNCTIONS NOT TO BE USED BY SDK
    */

    public addOrUpdateService(serviceId: string, serviceName: string) {
        if (!this.services) { this.services = []; }

        const serviceIndex = this.services.findIndex((service) => { return service.id === serviceId; });
        if (serviceIndex !== -1) {
            //do nothing for the moment
        }
        else { this.services.push({ id: serviceId, name: serviceName }); }
    }

    //update participant from snapshot data, where we dont have all the informations
    public addOrUpdateParticipantFromSnapshot(participant: BubbleConferenceParticipant) {
        if (this.localParticipant && this.localParticipant.id === participant.id) {
            return;
        }

        const participantIndex = this.participants.findIndex((part: BubbleConferenceParticipant) => { return part.id === participant.id; });
        if (participantIndex !== -1) {
            const existingParticipant = this.participants[participantIndex];
            existingParticipant.isVideoAvailable = participant.isVideoAvailable;
        }
        else { this.participants.push(participant); }
    }

    public addOrUpdateParticipant(participant: BubbleConferenceParticipant, updateRaiseHand = false) {
        if (this.localParticipant && this.localParticipant.id === participant.id) {
            this.localParticipant.mute = participant.mute;
            this.localParticipant.noMicrophone = participant.noMicrophone;
            if (updateRaiseHand) {
                this.localParticipant.raiseHand = participant.raiseHand;
                this.localParticipant.raiseHandDate = participant.raiseHandDate;
            }
            return;
        }

        const participantIndex = this.participants.findIndex((part: BubbleConferenceParticipant) => { return part.id === participant.id; });
        if (participantIndex !== -1) {
            const existingParticipant = this.participants[participantIndex];
            if (existingParticipant.mute !== participant.mute) {
                existingParticipant.mute = participant.mute;
                this.sendEvent(BubbleConferenceEvents.ON_PARTICIPANT_MUTE_CHANGE, { participant: existingParticipant });
            }
            existingParticipant.noMicrophone = participant.noMicrophone;
            if (updateRaiseHand) {
                this.localParticipant.raiseHand = participant.raiseHand;
                this.localParticipant.raiseHandDate = participant.raiseHandDate;
            }
        }
        else { this.participants.push(participant); }
    }

    public updateTalkingTimeForParticipant(id: string, talkingTime: number) {
        if (!this.talkingTimeStats[id]) {
            this.talkingTimeStats[id] = {
                talkingTime: talkingTime
            };
        }

        this.talkingTimeStats[id].talkingTime = talkingTime;
    }

    public startDuration() {
        if (this.durationTimer) return;
        // TODO: memory leak (need to be freed in onDestroy)
        this.durationTimer = setInterval(() => {
            this.duration++;
        }, 1000);
    }

    public stopDuration() {
        if (this.durationTimer) {
            clearInterval(this.durationTimer);
            this.durationTimer = null;
            this.duration = 0;
        }
    }

    public getPinnedParticipant(): any {
        //check if participant is still here
        if (this.pinnedParticipant) {
            const checkPinnedParticipant = this.getParticipantByIdAndMediaType(this.pinnedParticipant.id, this.pinnedParticipant.mediaType);
            if (!checkPinnedParticipant) this.pinnedParticipant = null;
        }

        return this.pinnedParticipant;
    }

    public updatePinnedParticipant(participant: BubbleConferenceParticipant = null, mediaType: string = ""): void {
        if (!participant) {
            this.pinnedParticipant = null;
            return;
        }
        //pin participant
        this.pinnedParticipant = {
            id: participant.id,
            mediaType: mediaType
        };
    }

    public setLocked(lock: boolean = false) {
        this.locked = lock;
    }

    public setSharingParticipant(participant: BubbleConferenceParticipant = null) {
        if (!participant && this.sharingParticipant?.videoSession) {
            this.sharingParticipant.videoSession.terminate();
            this.sharingParticipant.videoSession = null;
        }
        this.sharingParticipant = participant;
    }

    public updateRaiseHandParticipants(): void {
        const participantsToAdd = this.participants.filter((participant: BubbleConferenceParticipant) => { return participant.raiseHand; });
        if (this.localParticipant.raiseHand) { participantsToAdd.push(this.localParticipant); }

        this.raiseHandParticipants = participantsToAdd;
    }

    private getParticipantByIdAndMediaType(id: string, mediaType: string): BubbleConferenceParticipant {
        if (mediaType === "sharing") {
            if (this.localParticipant.id === id) { return (this.hasLocalSharing) ? this.localParticipant : null; }
            if (this.sharingParticipant && this.sharingParticipant.id === id) { return this.sharingParticipant; }
        }
        else {
            if (this.localParticipant.id === id) { return (this.localParticipant.videoSession) ? this.localParticipant : null; }
            const participant = this.participants.find((part: BubbleConferenceParticipant) => { return part.id === id; });
            if (participant && participant.videoSession) return participant;
        }

        return null;
    }

    public toJSON(): any {
        const json: any = {
            id: this.id,
            status: this.status,
            bubbleId: this.id,
            bubbleName: this.bubble.name
        };
        return json;
    }
}
